import type { PropsWithChildren } from 'react';

// Note: This needs to be imported first to prevent a MUI/vite bug from surfacing.
// See: https://github.com/mui/material-ui/issues/31835
// eslint-disable-next-line import/order
import ThemeProvider from '@blueshift-ui/theme/dist/components/provider';

import { AnalyticsProvider } from '@blueshift-ui/analytics';
import ConfigProvider from '@blueshift-ui/core/dist/providers/config-provider';
import FetchProvider from '@blueshift-ui/fetch/dist/components/fetch-provider';
import { HelmetProvider } from 'react-helmet-async';
import I18nProvider from '@blueshift-ui/i18n/dist/components/provider';
import UserAccessBoundary from './components/user-access-boundary';
import ZendeskChatWidget from '../components/chat-widget';

const APP_CONFIG = {
  host: `https://${process.env.VITE_APP_HOST}`,
  apiHost: process.env.VITE_API_BASE_URL,
  launchDarklyAppKey: process.env.VITE_LAUNCH_DARKLY_APP_KEY,
  launchDarklyApiBaseUrl: process.env.VITE_LAUNCH_DARKLY_API_BASE_URL,
};

const SESSION_REPLAY_OPTIONS = { sampleRate: 0.25 };

function Providers({ children }: Readonly<PropsWithChildren<unknown>>) {
  const inPreviewMode = process.env.VITE_MODE !== 'production';

  return (
    <ConfigProvider config={APP_CONFIG}>
      <ThemeProvider>
        <HelmetProvider>
          <I18nProvider namespace="client-account-ui" preview={inPreviewMode}>
            <FetchProvider>
              <AnalyticsProvider
                sessionReplayKey={`${process.env.VITE_AMPLITUDE_API_KEY ?? ''}`}
                sessionReplayOptions={SESSION_REPLAY_OPTIONS}
                writeKey={process.env.VITE_SEGMENT_API_KEY}
              >
                <UserAccessBoundary>
                  <ZendeskChatWidget>{children}</ZendeskChatWidget>
                </UserAccessBoundary>
              </AnalyticsProvider>
            </FetchProvider>
          </I18nProvider>
        </HelmetProvider>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default Providers;
